/* ==========================================================================
   assets/tb-mnav.js — behaviour for the TrendyBits mobile navigation
   ==========================================================================

   Loaded from snippets/meta-tags.liquid with `defer`. Pairs with
   snippets/tb-mobile-nav.liquid and assets/tb-mnav.css.

   WHAT THIS FILE IS *NOT* RESPONSIBLE FOR
   ---------------------------------------
   Opening and closing the menu. sections/tb-header.liquid already does that,
   and it selects by data-attribute rather than by class:

       [data-tb-drawer]  [data-tb-drawer-overlay]
       [data-tb-drawer-open]  [data-tb-drawer-close]

   The new markup keeps every one of those attributes, so the hamburger, the
   close button, the backdrop click and the Escape key all still work through
   the existing header script. Nothing was duplicated here.

   WHAT IT DOES DO
   ---------------
   1. TABS. One attribute, data-tab="shop|categories|seasons", set on
      .tb-mnav. Every visual consequence (which pane shows, which pill is
      yellow, whether the Crazy Deals hero is visible) is pure CSS keyed off
      that attribute. This file only writes the attribute.

   2. LEVEL-2 PANELS. A universe card that has sub-categories carries
      data-mnav-open="<handle>"; clicking it slides in the matching
      [data-mnav-l2="<handle>"] panel instead of navigating. A universe with
      no sub-categories has no such attribute and behaves as a plain link.
      The panel's own hero link is how you actually reach the collection.

   3. SEASONS. Reads the JSON payload the snippet prints, works out which
      season is featured, and renders the hero plus the countdown rows. The
      rules are copied from sections/tb-seasons.liquid so the menu and the
      homepage can never disagree:

        - all dates evaluated in Egypt time (+02:00)
        - a season "incoming" (inside its lead window) outranks a live one
        - otherwise the live season ending soonest is featured
        - the rest are ordered by whichever date comes next
        - undated seasons sort last and read "Coming soon"

      Dates live in DEFAULTS below, exactly as in tb-seasons.liquid. Ramadan
      and Eid are lunar — they need updating once a year in BOTH files.
   ========================================================================== */

(function () {
  'use strict';

  var root = document.querySelector('.tb-mnav');
  if (!root) return;

  /* ------------------------------------------------------------------ *
   * 1. TABS
   * ------------------------------------------------------------------ */

  var homeBtn = root.querySelector('[data-mnav-home]');

  function setTab(name) {
    root.setAttribute('data-tab', name);
    /* the mockup lights Home up on the Shop tab only */
    if (homeBtn) homeBtn.classList.toggle('is-on', name === 'shop');
    root.querySelectorAll('[data-mnav-tab]').forEach(function (b) {
      b.setAttribute('aria-selected', b.getAttribute('data-mnav-tab') === name ? 'true' : 'false');
    });
    var sc = root.querySelector('.tbm-panel--root .tbm-scroll');
    if (sc) sc.scrollTop = 0;
  }

  root.querySelectorAll('[data-mnav-tab]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      setTab(btn.getAttribute('data-mnav-tab'));
    });
  });

  setTab(root.getAttribute('data-tab') || 'shop');

  /* ------------------------------------------------------------------ *
   * 2. LEVEL-2 PANELS
   * ------------------------------------------------------------------ */

  function closeL2() {
    root.querySelectorAll('.tbm-panel--l2.is-open').forEach(function (p) {
      p.classList.remove('is-open');
      p.setAttribute('aria-hidden', 'true');
    });
  }

  root.querySelectorAll('[data-mnav-open]').forEach(function (card) {
    card.addEventListener('click', function (e) {
      var panel = root.querySelector('[data-mnav-l2="' + card.getAttribute('data-mnav-open') + '"]');
      if (!panel) return;               /* no panel built -> let the link work */
      e.preventDefault();
      closeL2();
      panel.classList.add('is-open');
      panel.setAttribute('aria-hidden', 'false');
      var sc = panel.querySelector('.tbm-scroll');
      if (sc) sc.scrollTop = 0;
    });
  });

  root.querySelectorAll('[data-mnav-back]').forEach(function (btn) {
    btn.addEventListener('click', closeL2);
  });

  /* When the menu itself closes, drop back to the root panel and the Shop
     tab so the next open always starts from the same place. The header owns
     the actual close, so watch the class rather than duplicating handlers. */
  new MutationObserver(function () {
    if (!root.classList.contains('is-open')) {
      window.setTimeout(function () {
        if (root.classList.contains('is-open')) return;
        closeL2();
        setTab('shop');
      }, 320);
    }
  }).observe(root, { attributes: true, attributeFilter: ['class'] });

  /* Escape backs out of a level-2 panel first, and only closes the whole
     menu on the second press (the header's own Escape handler). */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    if (root.querySelector('.tbm-panel--l2.is-open')) {
      e.stopPropagation();
      closeL2();
    }
  }, true);

  /* ------------------------------------------------------------------ *
   * 3. SEASONS
   * ------------------------------------------------------------------ */

  var heroEl = root.querySelector('[data-mnav-ssn-hero]');
  var rowsEl = root.querySelector('[data-mnav-ssn-rows]');
  var jsonEl = root.querySelector('[data-mnav-ssn-json]');
  if (!heroEl || !rowsEl || !jsonEl) return;

  var raw = [];
  try { raw = JSON.parse(jsonEl.textContent) || []; } catch (err) { raw = []; }
  if (!raw.length) return;

  var EG = '+02:00';                                     /* Egypt */
  var PALETTE = ['#ff9a3d', '#00fced', '#34d399', '#ff3d8a', '#a855f7', '#fed100'];

  /* Same table as sections/tb-seasons.liquid. Keep the two in sync. */
  var DEFAULTS = {
    'summer-offers':  { start: '2026-06-21', end: '2026-09-22', lead: 7 },
    'back-to-school': { start: '2026-09-20', end: '2026-10-15', lead: 10 },
    'christmas-1':    { start: '2026-12-25', end: '2026-12-26', lead: 14 },
    'valentine':      { start: '2027-02-14', end: '2027-02-15', lead: 14 },
    'ramadan':        { start: '2027-02-18', end: '2027-03-19', lead: 10 },
    'eid':            { start: '2027-03-20', end: '2027-03-23', lead: 10 },
    'mothers-day':    { start: '2027-03-21', end: '2027-03-22', lead: 10 }
  };

  var SEASONS = raw.map(function (s, i) {
    var d = DEFAULTS[s.handle] || {};
    var start = d.start ? new Date(d.start + 'T00:00:00' + EG) : null;
    var end   = d.end   ? new Date(d.end   + 'T23:59:59' + EG)
                        : (start ? new Date(start.getTime() + 86400000) : null);
    if (start && isNaN(start)) start = null;
    if (end && isNaN(end)) end = null;
    var lead = d.lead != null ? d.lead : 14;
    return {
      name:  s.name,
      emoji: s.emoji || '✨',
      img:   s.img || '',
      url:   s.url || '#',
      count: s.count || 0,
      acc:   PALETTE[i % PALETTE.length],
      start: start,
      end:   end,
      spot:  start ? new Date(start.getTime() - lead * 86400000) : null,
      dated: !!start
    };
  });

  var MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  var OFFMS = (function (o) {
    var m = /([+-])(\d{2}):?(\d{2})/.exec(o || '');
    return m ? (m[1] === '-' ? -1 : 1) * (((+m[2]) * 60) + (+m[3])) * 60000 : 0;
  })(EG);

  function md(d) {
    var x = new Date(d.getTime() + OFFMS);
    return x.getUTCDate() + ' ' + MON[x.getUTCMonth()];
  }

  function esc(s) {
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function pick(now) {
    SEASONS.forEach(function (s) {
      s.live     = s.dated && now >= s.start && now <= s.end;
      s.incoming = s.dated && now < s.start && now >= s.spot;
      s.upcoming = s.dated && now < s.start;
    });
    var inc  = SEASONS.filter(function (s) { return s.incoming; }).sort(function (a, b) { return a.start - b.start; });
    var live = SEASONS.filter(function (s) { return s.live; }).sort(function (a, b) { return a.end - b.end; });
    var featured;
    if (inc.length) featured = inc[0];
    else if (live.length) featured = live[0];
    else {
      var up = SEASONS.filter(function (s) { return s.upcoming; }).sort(function (a, b) { return a.start - b.start; });
      featured = up[0] || SEASONS[0];
    }
    function nextDate(s) {
      if (!s.dated) return new Date(8640000000000000);
      if (now < s.start) return s.start;
      if (now <= s.end)  return s.end;
      return new Date(s.start.getTime() + 31536000000);
    }
    var rest = SEASONS.filter(function (s) { return s !== featured; })
                      .sort(function (a, b) { return nextDate(a) - nextDate(b); });
    return { featured: featured, rest: rest };
  }

  function parts(ms) {
    if (ms < 0) ms = 0;
    return {
      d: Math.floor(ms / 86400000),
      h: Math.floor(ms / 3600000) % 24,
      m: Math.floor(ms / 60000) % 60
    };
  }

  function cdBoxes(ms) {
    var p = parts(ms);
    return '<span class="tbm-cd"><b>' + p.d + '</b><i>days</i></span>' +
           '<span class="tbm-cd"><b>' + p.h + '</b><i>hrs</i></span>' +
           '<span class="tbm-cd"><b>' + p.m + '</b><i>min</i></span>';
  }

  var lastSig = '';

  function render() {
    var now = new Date();
    var r = pick(now), f = r.featured;
    var incoming = f.dated && now < f.start;
    var target = f.dated ? (incoming ? f.start.getTime() : f.end.getTime()) : 0;

    var sig = f.name + '|' + r.rest.map(function (s) { return s.name; }).join(',') + '|' + (incoming ? 'I' : 'L');

    if (sig !== lastSig) {
      lastSig = sig;

      /* ---- hero ---- */
      var tag = incoming
        ? '<span class="tbm-live__tag tbm-live__tag--soon"><span class="tbm-pulse"></span> Drops ' + esc(md(f.start)) + '</span>'
        : '<span class="tbm-live__tag"><span class="tbm-pulse"></span> Live now</span>';

      heroEl.innerHTML =
        '<a class="tbm-live" href="' + esc(f.url) + '">' +
          (f.img ? '<img src="' + esc(f.img) + '" alt="' + esc(f.name) + '" loading="lazy">' : '') +
          '<span class="tbm-live__b">' +
            tag +
            '<span class="tbm-live__t">' + esc(f.emoji) + ' ' + esc(f.name) + '</span>' +
            '<span class="tbm-live__row">' +
              '<span class="tbm-live__cd" data-mnav-cd>' + (f.dated ? cdBoxes(target - now.getTime()) : '') + '</span>' +
              '<span class="tbm-live__go">→</span>' +
            '</span>' +
          '</span>' +
        '</a>';

      /* ---- rows ---- */
      var soonMarked = false;
      var html = '';
      r.rest.forEach(function (s) {
        var live = s.dated && now >= s.start && now <= s.end;
        var cls = 'tbm-srow';
        var sub, num, unit;

        if (!s.dated) {
          sub = 'Coming soon'; num = ''; unit = '';
        } else if (live) {
          cls += ' tbm-srow--live';
          sub = 'Ends ' + md(s.end);
          num = parts(s.end - now).d; unit = num === 1 ? 'day left' : 'days left';
        } else {
          if (!soonMarked) { cls += ' tbm-srow--soon'; soonMarked = true; }
          sub = 'Opens ' + md(s.start);
          num = parts(s.start - now).d; unit = num === 1 ? 'day' : 'days';
        }

        /* Badge policy for the small season rows is EMOJI FIRST, photo second —
           the opposite of the big hero above, which is photo first. A 34px
           rounded square is too small for a product photo to read as anything,
           and the mockup shows a flat emoji in every row. The collection image
           is only used when the season has no emoji at all. */
        var badge = s.emoji
          ? esc(s.emoji)
          : (s.img ? '<img src="' + esc(s.img) + '" alt="" loading="lazy">' : '');

        html +=
          '<a class="' + cls + '" href="' + esc(s.url) + '">' +
            '<span class="tbm-srow__e">' + badge + '</span>' +
            '<span class="tbm-srow__b">' +
              '<span class="tbm-srow__t">' + esc(s.name) + '</span>' +
              '<span class="tbm-srow__s">' + esc(sub) + '</span>' +
            '</span>' +
            (num === '' ? '' : '<span class="tbm-srow__d"><b>' + num + '</b><i>' + unit + '</i></span>') +
          '</a>';
      });
      rowsEl.innerHTML = html;
    } else {
      /* cheap path: only the hero countdown moves between full re-renders */
      var cd = heroEl.querySelector('[data-mnav-cd]');
      if (cd && f.dated) cd.innerHTML = cdBoxes(target - now.getTime());
    }
  }

  render();
  window.setInterval(render, 30000);
})();